<script setup lang="ts">
/**
 * First step of the login (UC-02 step 1, ADR-078): the e-mail address, filled in with the one
 * the browser remembers. The address is checked with the contract before it leaves; an accepted
 * address is remembered for the next login and handed to the dialog, which asks the API what
 * comes next. Labels and hint after the design prototype (`#siLogin`).
 */
import { ref } from "vue";
import { LoginEmail } from "@ai-platform/contracts";
import PixButton from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixButton/PixButton.vue";
import PixTextField from "@pixelpoint-pattern-library-v2/base/components/styled/base/inputs/PixTextField/PixTextField.vue";
import { readLoginEmail, writeLoginEmail } from "../lib/login-email.ts";
import { browserStorage } from "../lib/storage.ts";

const props = defineProps<{
  /** a start is under way; the button waits */
  pending: boolean;
  /** the address of this dialog's last lookup, for when the storage remembers none (blocked) */
  initial?: string | undefined;
}>();

const emit = defineEmits<{
  /** the normalised address, already remembered */
  submit: [email: string];
}>();

const email = ref(readLoginEmail(browserStorage()) || (props.initial ?? ""));
const fieldError = ref("");

function submit() {
  const parsed = LoginEmail.safeParse(email.value);
  if (!parsed.success) {
    fieldError.value = "Bitte gebt eure E-Mail-Adresse ein, etwa vorname.nachname@organisation.at.";
    return;
  }
  fieldError.value = "";
  writeLoginEmail(browserStorage(), parsed.data);
  emit("submit", parsed.data);
}
</script>

<template>
  <form class="flex flex-col gap-3" novalidate @submit.prevent="submit">
    <PixTextField
      v-model="email"
      type="email"
      label="E-Mail-Adresse"
      placeholder="vorname.nachname@organisation.at"
      :error="fieldError"
      required
    />
    <p class="text-meta text-text-soft m-0">
      Ihr arbeitet schon mit dataCycle? Dann gelten dieselben Zugangsdaten. Beim ersten Mal entsteht euer Zugang von
      selbst.
    </p>
    <PixButton type="submit" class="text-ui mt-1 min-h-(--size-button) w-full font-bold" :disabled="props.pending">
      Anmelden
    </PixButton>
  </form>
</template>
